import { create } from 'zustand';
import type { ViewMode, TileColumns, SortField, SortDirection, Theme, SystemCategoryId, SmartFilterId } from 'markcat-domain';

interface UIState {
  theme: Theme;
  viewMode: ViewMode;
  tileColumns: TileColumns;
  tileCarousel: boolean;
  tileClick: 'drawer' | 'link';
  favouritesLanding: boolean;
  sortField: SortField;
  sortDirection: SortDirection;
  selectionMode: boolean;
  selectedIds: Set<string>;

  // Category filter — shared between the persistent shell sidebar and the
  // bookmark list so they always agree. One active dimension at a time.
  selectedCategory: string | null;
  selectedSystemCat: SystemCategoryId | null;
  selectedSmartFilter: SmartFilterId | null;
  selectedTag: string | null;

  // One-shot handoff from the /app/share route: a shared URL whose create
  // failed, waiting to be prefilled into the Add bar. Consumed (cleared) by
  // BookmarkManager on mount. Not persisted.
  pendingShareUrl: string | null;

  setTheme: (theme: Theme) => void;
  setViewMode: (mode: ViewMode) => void;
  setTileColumns: (cols: TileColumns) => void;
  setTileCarousel: (v: boolean) => void;
  setTileClick: (v: 'drawer' | 'link') => void;
  setFavouritesLanding: (v: boolean) => void;
  setSortField: (field: SortField) => void;
  setSortDirection: (dir: SortDirection) => void;
  toggleSelectionMode: () => void;
  toggleSelected: (id: string) => void;
  selectAll: (ids: string[]) => void;
  clearSelection: () => void;
  selectCategory: (path: string | null) => void;
  selectSystemCat: (id: SystemCategoryId | null) => void;
  selectSmartFilter: (id: SmartFilterId | null) => void;
  selectTag: (tag: string | null) => void;
  setPendingShareUrl: (url: string | null) => void;
}

/** localStorage stores the column choice as a string; map it back to 'auto' or
 *  a 1–5 number, defaulting to 'auto' for anything unrecognized. */
function parseTileColumns(raw: string | null): TileColumns {
  if (raw === '1' || raw === '2' || raw === '3' || raw === '4' || raw === '5') {
    return Number(raw) as TileColumns;
  }
  return 'auto';
}

/** The four filter dimensions are mutually exclusive; every setter spreads this
 *  to clear the others before applying its own. */
const noFilter = { selectedCategory: null, selectedSystemCat: null, selectedSmartFilter: null, selectedTag: null };

/** Mirror the effective theme onto the <meta name="theme-color"> pair so the
 *  browser chrome (status bar, installed-PWA title bar) matches the app. Both
 *  tags get the same value — the media attributes only serve the pre-JS paint. */
export function syncThemeColorMeta(theme: Theme) {
  const dark =
    theme === 'midnight' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const content = dark ? '#0a0e16' : '#a84b26';
  document.querySelectorAll('meta[name="theme-color"]').forEach((el) => el.setAttribute('content', content));
}

export const useUIStore = create<UIState>((set) => ({
  // Default 'system' matches main.tsx's pre-render theme attribute. A
  // mismatched default caused a brief flash where data-theme was set to
  // 'system' while the store reported 'dusk'.
  theme: (localStorage.getItem('mc_theme') as Theme) || 'system',
  viewMode: (localStorage.getItem('mc_view_mode') as ViewMode) || 'list',
  tileColumns: parseTileColumns(localStorage.getItem('mc_tile_columns')),
  tileCarousel: localStorage.getItem('mc_tile_carousel') === 'true',
  tileClick: (localStorage.getItem('mc_tile_click') as 'drawer' | 'link') || 'drawer',
  favouritesLanding: localStorage.getItem('mc_favourites_landing') === 'true',
  sortField: (localStorage.getItem('mc_sort_field') as SortField) || 'date',
  sortDirection: (localStorage.getItem('mc_sort_dir') as SortDirection) || 'desc',
  selectionMode: false,
  selectedIds: new Set(),
  selectedCategory: null,
  selectedSystemCat: null,
  selectedSmartFilter: null,
  selectedTag: null,
  pendingShareUrl: null,

  setTheme: (theme) => {
    localStorage.setItem('mc_theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
    syncThemeColorMeta(theme);
    set({ theme });
  },
  setViewMode: (viewMode) => {
    localStorage.setItem('mc_view_mode', viewMode);
    set({ viewMode });
  },
  setTileColumns: (tileColumns) => {
    localStorage.setItem('mc_tile_columns', String(tileColumns));
    set({ tileColumns });
  },
  setTileCarousel: (tileCarousel) => {
    localStorage.setItem('mc_tile_carousel', String(tileCarousel));
    set({ tileCarousel });
  },
  setTileClick: (tileClick) => {
    localStorage.setItem('mc_tile_click', tileClick);
    set({ tileClick });
  },
  setFavouritesLanding: (favouritesLanding) => {
    localStorage.setItem('mc_favourites_landing', String(favouritesLanding));
    set({ favouritesLanding });
  },
  setSortField: (sortField) => {
    localStorage.setItem('mc_sort_field', sortField);
    set({ sortField });
  },
  setSortDirection: (sortDirection) => {
    localStorage.setItem('mc_sort_dir', sortDirection);
    set({ sortDirection });
  },
  toggleSelectionMode: () => set((s) => ({
    selectionMode: !s.selectionMode,
    selectedIds: s.selectionMode ? new Set() : s.selectedIds,
  })),
  toggleSelected: (id) => set((s) => {
    const next = new Set(s.selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    return { selectedIds: next };
  }),
  selectAll: (ids) => set({ selectedIds: new Set(ids) }),
  clearSelection: () => set({ selectedIds: new Set() }),
  selectCategory: (path) => set({ ...noFilter, selectedCategory: path }),
  selectSystemCat: (id) => set({ ...noFilter, selectedSystemCat: id }),
  selectSmartFilter: (id) => set({ ...noFilter, selectedSmartFilter: id }),
  selectTag: (tag) => set({ ...noFilter, selectedTag: tag }),
  setPendingShareUrl: (pendingShareUrl) => set({ pendingShareUrl }),
}));
